import { logger } from '../../utils/logger'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { identity, map, Observable, of, startWith, takeLast } from 'rxjs'
import { ExaminadorUnicamp } from 'src/app/models/examinador-interno'
import { ConcursosAtivos } from 'src/app/models/concursos-ativos'
import { ErrorHandlerService } from 'src/app/services/error-handler.service'
import { UnespCoreEnvironment } from 'src/libs/unesp-core'
import { ExaminadorExterno } from 'src/app/models/examinador-externo'
import { ComissaoNotaProvaInscricaoDTO } from 'src/app/models/ComissaoNotaProvaInscricaoDTO'
import { ComissaoNotaProvaInscricaoRequest } from 'src/app/models/ComissaoNotaProvaInscricaoRequest'
import { Page } from 'src/app/models/page.model'
import { EnviarNotasEnvelopeDTO } from 'src/app/models/EnviarNotasEnvelopeDTO'
import { EnviarNotasEnvelopeResponseDTO } from 'src/app/models/EnviarNotasEnvelopeResponseDTO'
import { ExaminadorExternoConsulta } from 'src/app/models/examinador-externo-consulta.model'
import { ExaminadorUnicampConsulta } from 'src/app/models/examinador-unicamp-consulta'
import { CadastroExaminadorDeAcordo } from 'src/app/models/cadastro-examinador-deacordo.model'
import { ComissaoResultadoInscricaoDTO } from 'src/app/models/ComissaoResultadoInscricaoDTO'
import { ResultadoClassificacaoDTO } from 'src/app/models/ResultadoClassificacaoDTO '
import { ListagemExaminador } from 'src/app/models/listagem-examinador'
import { DadosTermoConflito, DadosTermoConflitoDeInteresseDTO } from 'src/app/models/registro-de-acordo-examinador'

@Injectable({
  providedIn: 'root',
})
export class ComissaoJulgadoraService {
  private readonly baseUrl: string

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  buscarPessoa(tipoDeBusca: string, valor: string): Observable<ExaminadorUnicampConsulta> {
    const httpParams = new HttpParams().set(tipoDeBusca, valor)

    return this.http.get<ExaminadorUnicampConsulta>(`${this.baseUrl}/comissao/examinador/interno`, {
      params: httpParams,
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  buscarConcursosAtivos(): Observable<ConcursosAtivos[]> {
    return this.http.get<ConcursosAtivos[]>(`${this.baseUrl}/concurso/ativos`)
  }

  cadastrarExaminadorInterno(form: ExaminadorUnicamp): Observable<ExaminadorUnicamp> {
    return this.http.post<ExaminadorUnicamp>(`${this.baseUrl}/comissao/examinador`, form)
  }

  cadastrarExaminadorExterno(form: any): Observable<ExaminadorExterno> {
    return this.http.post<ExaminadorExterno>(`${this.baseUrl}/comissao/examinador/externo`, form)
  }

  consultarExaminadorExterno(searchType: string, searchValue: string): Observable<ExaminadorExternoConsulta> {
    return this.http.get<ExaminadorExternoConsulta>(
      `${this.baseUrl}/comissao/examinador/externo?${searchType}=${searchValue}`
    )
  }

  listarExaminadores(fnParams: {
    concurso?: number
    papel?: string
    search?: string
    page?: number
    size?: number
    sortField?: string
    sortDirection?: string
  }): Observable<Page<ListagemExaminador>> {
    const { concurso, papel, search, page = 0, size = 10, sortField = 'concursoName', sortDirection = 'asc' } = fnParams

    const params: any = { page, size, sortField, sortDirection }

    if (concurso) params.concurso = concurso
    if (papel) params.papel = papel
    if (search) params.search = search

    return this.http
      .get<Page<ListagemExaminador>>(`${this.baseUrl}/comissao/examinador`, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  listarConcursos(search = '', page = 0, size = 10, filtroTipoConcursoSelecionado = '', sortField = 'inicio', sortDirection = 'desc'): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/comissao/concurso`, {
      params: new HttpParams()
        .set('page', page.toString())
        .set('size', size.toString())
        .set('tipoConcurso', filtroTipoConcursoSelecionado)
        .set('sortField', sortField)
        .set('sortDirection', sortDirection),
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  listarProvasExaminador(idConcurso: number): Observable<any> {
    const params = new HttpParams().set('concursoId', idConcurso.toString())
    return this.http.get<any>(`${this.baseUrl}/comissao/prova`, { params })
  }

  calcularMediaExaminadores(idConcurso: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/comissao/media-examinador`, idConcurso)
  }

  atualizarConceito(maloteId: number, payload: { conceito: string }) {
    return this.http.patch(`${this.baseUrl}/comissao/malote/${maloteId}/conceito`, payload)
  }

  atualizarConceitoPresidente(resultadoId: number, payload: { conceito: string }) {
    return this.http.patch(`${this.baseUrl}/comissao-resultado/${resultadoId}/conceito`, payload)
  }

  listarNotas(idConcurso: number, tipoProva: string): Observable<ComissaoNotaProvaInscricaoDTO[]> {
    logger.log('Chegou aqui')
    const params = new HttpParams().set('concursoId', idConcurso.toString()).set('tipoProva', tipoProva)
    return this.http.get<ComissaoNotaProvaInscricaoDTO[]>(`${this.baseUrl}/comissao-prova/notas`, { params })
  }

  registrarNota(request: ComissaoNotaProvaInscricaoRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/comissao-prova/nota`, request)
  }

  enviarEnvelope(dto: EnviarNotasEnvelopeDTO): Observable<EnviarNotasEnvelopeResponseDTO> {
    return this.http.post<EnviarNotasEnvelopeResponseDTO>(`${this.baseUrl}/comissao-prova/envelope`, dto)
  }

  calcularMediaExaminador(concursoId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/comissao/media-examinador`, concursoId)
  }

  getResultadoFinal(concursoId: number): Observable<ComissaoResultadoInscricaoDTO[]> {
    return this.http.post<ComissaoResultadoInscricaoDTO[]>(`${this.baseUrl}/comissao-resultado/classificacao`, null, {
      params: { concursoId: concursoId.toString() },
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  consolidarClassificacao(concursoId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/comissao-resultado/classificacao-final`, null, {
      params: { concursoId: concursoId.toString() },
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  consolidarClassificacaoFase1(concursoId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/comissao-resultado/classificacao-fase1`, null, {
      params: { concursoId: concursoId.toString() },
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  getClassificacaoPorConcurso(concursoId: number) {
    return this.http.get<any>(
      `${this.baseUrl}/listagem/classificacao-presidente/${concursoId}`
    )
  }

  getClassificacaoFase1(concursoId: number): Observable<ResultadoClassificacaoDTO[]> {
    return this.http.get<ResultadoClassificacaoDTO[]>(
      `${this.baseUrl}/comissao-resultado/classificacao-fase1`,
      { params: { concursoId: concursoId.toString() } }
    )
  }

  getClassificacaoPassos(concursoId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/comissao-resultado/classificacao-passos`,
      { params: { concursoId: concursoId.toString() } }
    )
  }

  arquivoObjectURLPorPath(path: string, name: string): Observable<string> {
    return this.arquivoObjectURL(`${this.baseUrl}/comissao-prova/${path}/${name}`)
  }

  private arquivoObjectURL(url: string): Observable<string> {
    return this.request(url).pipe(
      map(response => {
        const mimeType = response.headers.get('Content-Type') || 'application/octet-stream'
        const binaryData = []
        binaryData.push(response.body)
        return window.URL.createObjectURL(new Blob(binaryData, { type: mimeType }))
      }),
      startWith(''),
      takeLast(1)
    )
  }

  private request(url: string): Observable<any> {
    return this.http
      .get<any>(url, { responseType: 'blob' as 'json', observe: 'response' })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public getDadosRegistrarDeAcordoExaminador(paramsfn: { comissaoJulgadoraId: number; token: string }) {
    const { comissaoJulgadoraId, token } = paramsfn

    return this.http
      .get<any>(`${this.baseUrl}/publico/comissao/${comissaoJulgadoraId}/registrar-de-acordo`, { params: { token } })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public buscarDadosTermoConflitoInteresse(paramsfn: { comissaoJulgadoraId: number; token: string }) {
    const { comissaoJulgadoraId, token } = paramsfn

    return this.http
      .get<DadosTermoConflitoDeInteresseDTO>(
        `${this.baseUrl}/publico/comissao/${comissaoJulgadoraId}/termo-conflito-de-interesse`,
        { params: { token } }
      )
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public postRegistrarDeAcordoExaminador(payload: { comissaoJulgadoraId: number; token: string }) {
    return this.http
      .post<any>(`${this.baseUrl}/comissao/registrar-de-acordo`, payload)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public notificarExaminador(paramsfn: { comissaoJulgadoraId: number }) {
    const { comissaoJulgadoraId } = paramsfn

    return this.http
      .post<ListagemExaminador>(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/notificar-examinador`, null)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public notificarExaminadores(paramsfn: { comissaoJulgadoraIds: number[] }) {
    const { comissaoJulgadoraIds: ids } = paramsfn

    return this.http
      .post<ListagemExaminador[]>(`${this.baseUrl}/comissao/notificar-examinador`, { ids })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  public subistituirSuplente(comissaoJulgadoraId: number, suplenteId: any, justificativa: any) {
    return this.http.post(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/substituir-titular`, {
      suplenteId,
      justificativa,
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  public fetchDocumentoPreview(comissaoJulgadoraId: number, token: string, form: DadosTermoConflito) {
    const params = new HttpParams().set('token', token)
    return this.http.post(`${this.baseUrl}/publico/comissao/${comissaoJulgadoraId}/declaracao/preview`, form, { params, responseType: 'blob' })
  }

  public fetchDocumentoDeAcordo(form: DadosTermoConflito) {
    return this.http.post(`${this.baseUrl}/comissao/declaracao`, form, { responseType: 'blob' })
  }

  public sendDeAcordoDocument(comissaoJulgadoraId: number, emailToken: string, pdf: Blob) {
    const params = new HttpParams().set('comissaoJulgadoraId', comissaoJulgadoraId.toString()).set('token', emailToken)

    const formData = new FormData()
    formData.append('declaracaoAssinada', pdf)

    return this.http.post(`${this.baseUrl}/comissao/registrar-de-acordo`, formData, { params })
  }

  buscarResumoNotas(concursoId: number, fase?: string): Observable<any[]> {
    const params: any = { concursoId: concursoId.toString() };
    if (fase) params.fase = fase;
    return this.http.get<any[]>(`${this.baseUrl}/comissao-prova/resumo-notas`, { params });
  }

  buscarResumoNotasExaminador(concursoId: number, fase: string): Observable<any[]> {
    const params = new HttpParams()
      .set('concursoId', concursoId.toString())
      .set('fase', fase);
    return this.http.get<any[]>(`${this.baseUrl}/comissao-prova/resumo-notas-examinador`, { params });
  }

  buscarResumoNotasPresidente(concursoId: number): Observable<any[]> {
    const params = new HttpParams().set('concursoId', concursoId.toString());
    return this.http.get<any[]>(`${this.baseUrl}/comissao-prova/resumo-notas-presidente`, { params });
  }

  definirPresidente(comissaoJulgadoraId: number): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/define-presidente`, null)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  listarDocumentosComplementares(inscricaoId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/comissao/documentos-complementares/${inscricaoId}`)
  }

  downloadDocumentoComplementar(inscricaoId: number, complementoId: string | number, chunkDownload: boolean): Observable<any> {
    const params = new HttpParams().set('chunkDownload', chunkDownload.toString())
    const options = chunkDownload ? { params } : { params, responseType: 'blob' as 'json' }
    return this.http.get<any>(`${this.baseUrl}/comissao/documento-complementar/${inscricaoId}/${complementoId}/download`, options)
  }

  listarDocumentosConcurso(concursoId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/comissao/documentos-complementares-concurso/${concursoId}`)
  }

  downloadDocumentoAnonimo(concursoId: number, token: string, chunkDownload: boolean): Observable<any> {
    const params = new HttpParams().set('chunkDownload', chunkDownload.toString())
    const options = chunkDownload ? { params } : { params, responseType: 'blob' as 'json' }
    return this.http.get<any>(`${this.baseUrl}/comissao/documento-complementar-anonimo/${concursoId}/${token}/download`, options)
  }

  listarTitularesTransferencia(comissaoJulgadoraId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/titulares-transferencia`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  transferirPresidencia(comissaoJulgadoraId: number, novoPresidenteId: number) {
    return this.http.post(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/transferir-presidencia`, {
      novoPresidenteId,
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  removerComissaoJulgadora(comissaoJulgadoraId: number, motivo: string | null): Observable<any> {
    const params = motivo ? new HttpParams().set('motivo', motivo) : new HttpParams()
    return this.http
      .delete(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/remover`, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  downloadDocumentoComplementarJurado(inscricaoId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/comissao/documento-complementar/${inscricaoId}/download-jurado`, {
      responseType: 'blob'
    }).pipe(identity, this.errorHandlerService.catchError())
  }

  atualizarNotasRecurso(recursoId: number, notas: any[]): Observable<any> {
    return this.http.put(`${this.baseUrl}/comissao/recurso/${recursoId}/atualizar-notas`, { notas })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  obterClassificacaoExaminadorPorFase(concursoId: number, fase: string): Observable<any> {
    const params = new HttpParams().set('fase', fase);
    return this.http.post<any>(`${this.baseUrl}/comissao/classificacao-examinador/fase`, concursoId, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  verificarPendenciasClassificacao(concursoId: number): Observable<any> {
    const params = new HttpParams().set('concursoId', concursoId.toString());
    return this.http.get<any>(`${this.baseUrl}/comissao-resultado/pendencias-classificacao`, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  obterClassificacaoFase2(concursoId: number): Observable<ResultadoClassificacaoDTO[]> {
    return this.http.get<ResultadoClassificacaoDTO[]>(
      `${this.baseUrl}/comissao-resultado/classificacao-fase2`,
      { params: { concursoId: concursoId.toString() } }
    )
  }

  verificarPendenciasProvaEmAndamento(concursoId: number): Observable<any> {
    const params = new HttpParams().set('concursoId', concursoId.toString());
    return this.http.get<any>(`${this.baseUrl}/comissao-resultado/pendencias-prova-em-andamento`, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  atualizarEmailExaminadorExterno(id: number, email: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/comissao/examinador/externo/${id}/email`, { email })
  }

  listarHistorico(concursoId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/comissao/concurso/${concursoId}/historico`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  getEstadoComissao(concursoId: number): Observable<string> {
    return this.http
      .get<string>(`${this.baseUrl}/comissao/concurso/${concursoId}/estado`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  trocarPapel(comissaoJulgadoraId: number, novoPapel: string): Observable<any> {
    return this.http
      .put(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/trocar-papel`, { novoPapel })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarAtesteManual(comissaoJulgadoraId: number, documentoSigad: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/comissao/${comissaoJulgadoraId}/ateste-manual`, { documentoSigad })
      .pipe(identity, this.errorHandlerService.catchError())
  }
}
